import { ElementRef, inject, Injectable, signal } from '@angular/core';
import { IPoint, IRect, PointExtensions } from '@foblex/2d';
import { EFConnectableSide, FDragStartedEvent, FFlowComponent, injectFlowState } from '@foblex/flow';
import { get } from 'lodash';
import { FlowNodeHelpers } from '../helpers/flow-node.helpers';
import { FlowStateConnection } from '../models/flow-connection.model';
import { FlowNode } from '../models/flow-node.model';
import { FlowApiService } from './flow-api.service';

@Injectable()
export class FlowDragAndDropService {
  isDragging = signal(false);
  private readonly state = injectFlowState<FlowNode, FlowStateConnection, FlowNode>();
  private readonly api = inject(FlowApiService);
  private constrainedNodes: FlowNode[] = [];
  private constrainedNodesInitialPositions: { [k: string]: IPoint } = {};

  public onDragStarted(_event: FDragStartedEvent): void {
    const state = this.api.getState();
    if (!state?.nodes) {
      return;
    }
    this.isDragging.set(true);
    this.constrainedNodes = [];
    this.constrainedNodesInitialPositions = {};
    const selectedNodes = get(state, ['selection', 'nodes'], []);

    for (const nodeId of selectedNodes) {
      const node = state.nodes[nodeId];
      if (!node) continue;

      this.constrainedNodesInitialPositions[node.id] = node.position;
      const childrenIds = FlowNodeHelpers.findChildren(node, Object.values(state.nodes)).map((x) => x.id);
      if (childrenIds?.length) {
        for (const childNodeId of childrenIds) {
          const childNode = state.nodes[childNodeId];
          if (childNode) {
            this.constrainedNodesInitialPositions[childNodeId] = childNode.position;
          }
        }
      }
    }
  }

  public onDragEnded(): void {
    const state = this.api.getState();
    if (!state?.nodes) {
      return;
    }
    // !important: should set dragging false first otherwise this will impact the state change behaviour
    this.isDragging.set(false);

    const allNodes = Object.values(state.nodes);
    const nodes: { [k: string]: { position: IPoint } } = {};
    for (const node of this.constrainedNodes) {
      if (!FlowNodeHelpers.isRootNode(node.type) && node.nodeDraggingDisabled) {
        nodes[node.id] = { position: this.constrainedNodesInitialPositions[node.id] };
        const childrenIds = FlowNodeHelpers.findChildren(node, allNodes).map((x) => x.id);
        if (childrenIds?.length) {
          for (const childNodeId of childrenIds) {
            nodes[childNodeId] = { position: this.constrainedNodesInitialPositions[childNodeId] };
          }
        }
      }
    }
    // Revert the constrained nodes; runs inside the drag batch so the move and its revert are one history step.
    this.state.moveNodes(Object.entries(nodes).map(([id, { position }]) => ({ id, position })));
  }

  public updateMouseTrackerPosition(flow: FFlowComponent, elementRef: ElementRef<HTMLElement>): void {
    if (!this.isDragging()) {
      return;
    }

    const currentState = this.api.getState();

    const selectedNodes = get(currentState, ['selection', 'nodes'], [])
      .map((key: string) => currentState.nodes[key])
      .filter((n: FlowNode) => !!n);

    this.constrainedNodes = selectedNodes.map((node: FlowNode) => {
      const constrainedPosition = { ...node.position };
      let disabled = false;
      const rulesList = node.nodeDraggingRules;

      if (rulesList && rulesList.length > 0) {
        const selectedNodeElement = elementRef.nativeElement.querySelector(`.f-node[data-f-node-id="${node.id}"]`);
        if (!selectedNodeElement) return { ...node };
        const selectedNodeRect = selectedNodeElement.getBoundingClientRect();

        for (const rule of rulesList) {
          if (!rule.siblingNodeId) continue;

          const siblingElement = elementRef.nativeElement.querySelector(`.f-node[data-f-node-id="${rule.siblingNodeId}"]`);
          if (!siblingElement) continue;

          const siblingRect = siblingElement.getBoundingClientRect();

          if (rule.direction === EFConnectableSide.LEFT) {
            const siblingPosition = flow.getPositionInFlow(PointExtensions.initialize(siblingRect.left, siblingRect.y));
            const selectedNodePosition: IRect = flow.getPositionInFlow(PointExtensions.initialize(selectedNodeRect.right, selectedNodeRect.y));

            if (siblingPosition.x < selectedNodePosition.x) {
              constrainedPosition.x = siblingPosition.x - (rule.distance || 10);
              disabled = true;
            }
          } else if (rule.direction === EFConnectableSide.RIGHT) {
            const siblingPosition = flow.getPositionInFlow(PointExtensions.initialize(siblingRect.right, siblingRect.y));
            const selectedNodePosition: IRect = flow.getPositionInFlow(PointExtensions.initialize(selectedNodeRect.left, selectedNodeRect.y));

            if (siblingPosition.x > selectedNodePosition.x) {
              constrainedPosition.x = siblingPosition.x + (rule.distance || 10);
              disabled = true;
            }
          } else if (rule.direction === EFConnectableSide.TOP) {
            const siblingPosition = flow.getPositionInFlow(PointExtensions.initialize(siblingRect.left, siblingRect.top));
            const selectedNodePosition: IRect = flow.getPositionInFlow(PointExtensions.initialize(selectedNodeRect.left, selectedNodeRect.bottom));

            if (siblingPosition.y < selectedNodePosition.y) {
              constrainedPosition.y = siblingPosition.y - (rule.distance || 10);
              disabled = true;
            }
          } else if (rule.direction === EFConnectableSide.BOTTOM) {
            const siblingPosition = flow.getPositionInFlow(PointExtensions.initialize(siblingRect.left, siblingRect.bottom));
            const selectedNodePosition: IRect = flow.getPositionInFlow(PointExtensions.initialize(selectedNodeRect.left, selectedNodeRect.top));

            if (siblingPosition.y > selectedNodePosition.y) {
              constrainedPosition.y = siblingPosition.y + (rule.distance || 10);
              disabled = true;
            }
          }
        }
      }

      return { ...node, nodeDraggingDisabled: disabled, position: constrainedPosition };
    });
  }
}
